import { Facebook, Instagram, Twitter, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '@/features/settings/hooks/use-settings';
import { useBranches } from '@/features/branches/hooks/use-branches';
import { Container } from '@/components/layout/container';
import { Badge } from '@/components/ui/badge';
import { MpesaIcon } from '@/components/ui/mpesa-icon';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';

export function Footer() {
  const { settings } = useSettings();
  const { data: branches } = useBranches();
  const currentYear = new Date().getFullYear();
  const mainBranch = branches?.find((branch) => branch.is_main) || branches?.[0];
  const tillNumber = mainBranch?.mpesa_shortcode || '';
  const mpesaLink = tillNumber ? `/mpesa?till=${encodeURIComponent(tillNumber)}` : '/mpesa';

  return (
    <footer className="bg-brand-900 pb-8 pt-16 text-brand-50">
      <Container>
        <div className="mb-8 grid gap-8 border-b border-brand-800 pb-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.logo_alt}
                  className="h-12 w-auto brightness-0 invert"
                />
              ) : (
                <span className="text-2xl font-display font-bold tracking-tight text-white">
                  {settings.business_name.split(' ')[0]}
                  <span className="text-brand-400">
                    {settings.business_name.split(' ').slice(1).join(' ')}
                  </span>
                </span>
              )}
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-brand-300">
              Premium wine and spirits delivered to your doorstep. Quality products,
              exceptional service, and fast delivery across Kenya.
            </p>
            <div className="flex gap-4">
              {settings.social_facebook && (
                <a
                  href={settings.social_facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-800 transition-colors hover:bg-brand-700"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {settings.social_instagram && (
                <a
                  href={settings.social_instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-800 transition-colors hover:bg-brand-700"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {settings.social_twitter && (
                <a
                  href={settings.social_twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-800 transition-colors hover:bg-brand-700"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {settings.social_tiktok && (
                <a
                  href={`https://tiktok.com/@${settings.social_tiktok.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-800 transition-colors hover:bg-brand-700"
                >
                  <span className="text-xs font-bold">TikTok</span>
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="mb-6 font-semibold text-white">Quick Links</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/catalog" className="text-sm text-brand-300 transition-colors hover:text-white">
                  Our Catalog
                </Link>
              </li>
              <li>
                <Link to="/cart" className="text-sm text-brand-300 transition-colors hover:text-white">
                  Shopping Cart
                </Link>
              </li>
              <li>
                <Link to="/checkout" className="text-sm text-brand-300 transition-colors hover:text-white">
                  Checkout
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-sm text-brand-300 transition-colors hover:text-white">
                  Admin Portal
                </Link>
              </li>
            </ul>

            <div className="mt-6">
              <Link
                to={mpesaLink}
                className="group block overflow-hidden rounded-2xl border border-safaricom-green/40 bg-gradient-to-br from-[#071a12] via-[#0d2b1d] to-[#0f5d37] p-3.5 shadow-[0_24px_60px_-34px_rgba(0,166,80,0.75)] transition-all hover:-translate-y-0.5 hover:border-safaricom-green sm:p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-white/10 p-2.5 ring-1 ring-white/15">
                      <MpesaIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white sm:text-base">M-Pesa Till</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 flex-shrink-0 text-white/70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>

                <div className="mt-3 rounded-xl border border-white/10 bg-black/25 px-3.5 py-3 sm:px-4">
                  <p className="font-mono text-xl font-bold tracking-[0.18em] text-white sm:text-2xl sm:tracking-[0.28em]">
                    {tillNumber || '3022071'}
                  </p>
                </div>
              </Link>
            </div>
          </div>

          <div>
            <h4 className="mb-6 font-semibold text-white">Support</h4>
            <ul className="space-y-4">
              {settings.support_phone && (
                <li className="flex items-center gap-3 text-sm text-brand-300">
                  <Phone className="h-4 w-4 text-brand-400" />
                  <a href={`tel:${settings.support_phone}`} className="transition-colors hover:text-white">
                    {settings.support_phone}
                  </a>
                </li>
              )}
              {settings.whatsapp_business_number && (
                <li className="flex items-center gap-3 text-sm text-brand-300">
                  <WhatsAppIcon className="h-4 w-4" />
                  <a
                    href={`https://wa.me/${settings.whatsapp_business_number.replace(/\+/g, '')}`}
                    className="transition-colors hover:text-white"
                  >
                    WhatsApp Support
                  </a>
                </li>
              )}
              <li className="flex items-center gap-3 text-sm text-brand-300">
                <MapPin className="h-4 w-4 text-brand-400" />
                <span>Nairobi, Kenya</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-6 font-semibold text-white">About Us</h4>
            <div className="rounded-xl border border-brand-700/50 bg-brand-800/50 p-5">
              <p className="text-xs leading-relaxed text-brand-300">
                Karebe Wines & Spirits is dedicated to providing a curated selection of global and
                local brands. Responsible drinking and quality assurance are our core values.
              </p>
              <div className="mt-4 flex items-center gap-2 border-t border-brand-700 pt-4">
                <Badge
                  variant="success"
                  className="border-green-500/20 bg-green-500/10 text-[10px] text-green-400"
                >
                  Open Now
                </Badge>
                <span className="text-[10px] text-brand-400">Fast Delivery Available</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 text-xs text-brand-400 md:flex-row">
          <p>© {currentYear} {settings.business_name}. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white">
              Refund Policy
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
}
